import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { InfoOutlined, ArrowForward, LightbulbOutlined, CheckCircleOutline, LocationOnOutlined } from '@mui/icons-material';
import '../styles/SeoPages.css';
import '../styles/common.css';

const GoldRateRajasthanPage = () => {
    useEffect(() => {
        document.title = "Gold Rate in Rajasthan Today (22K & 24K) | MAB Jewels";

        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.name = "description";
            document.head.appendChild(metaDesc);
        }
        metaDesc.setAttribute("content", "Check today's gold rate in Rajasthan for 22K & 24K gold. City-wise prices, buying tips & trusted guidance from MAB Jewels, Jodhpur.");

        // Schema
        const schemaData = {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Gold Rate in Rajasthan Today (22K & 24K Gold Prices)",
            "description": "Check today's gold rate in Rajasthan for 22K & 24K gold. City-wise prices, buying tips & trusted guidance from MAB Jewels, Jodhpur.",
            "author": {
                "@type": "Organization",
                "name": "MAB Jewels"
            },
            "publisher": {
                "@type": "Organization",
                "name": "MAB Jewels",
            },
            "datePublished": "2025-12-25",
            "dateModified": new Date().toISOString().split('T')[0]
        };

        const faqSchema = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "What is today's gold rate in Rajasthan?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Gold rates change daily. Always check the latest updated gold price before buying jewellery or investing."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Is gold cheaper in Rajasthan?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Gold prices are similar across India. The difference lies in jeweller transparency, purity, and making charges."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Which gold purity is best for jewellery?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "22K gold is ideal for jewellery. 24K gold is best suited for investment."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Where can I buy certified gold in Rajasthan?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Buying from a BIS-hallmarked and trusted jeweller like MAB Jewels ensures purity and fair pricing."
                    }
                }
            ]
        };

        const script1 = document.createElement('script');
        script1.type = "application/ld+json";
        script1.text = JSON.stringify(schemaData);
        document.head.appendChild(script1);

        const script2 = document.createElement('script');
        script2.type = "application/ld+json";
        script2.text = JSON.stringify(faqSchema);
        document.head.appendChild(script2);

        return () => {
            if (document.head.contains(script1)) document.head.removeChild(script1);
            if (document.head.contains(script2)) document.head.removeChild(script2);
        };
    }, []);

    return (
        <div className="page-container seo-page">
            <div className="seo-content-wrapper">
                <h1>Gold Rate in Rajasthan Today (22K & 24K Gold Prices)</h1>

                <p className="last-updated-tag" style={{ fontSize: '0.9rem', color: '#666', marginTop: '-10px', marginBottom: '20px' }}>
                    Last Updated: 25 December 2025 | Source: Indian Bullion Market & Local Rajasthan Jewellery Markets
                </p>

                <p className="seo-intro">
                    Gold prices in Rajasthan change every day based on global bullion trends, currency movements, and local demand. Whether you are planning to buy jewellery for a wedding or invest in gold, knowing <strong>today's gold rate in Rajasthan</strong> helps you make the right decision at the right time.
                </p>

                <p>
                    At <strong>MAB Jewels</strong>, we believe customers deserve transparent pricing, certified purity, and honest guidance — the same values that have defined the legacy of <strong>Manoj Abhushan Bhandar</strong> for decades.
                </p>

                <h2>Today's Gold Price in Rajasthan (22K & 24K)</h2>
                <p>Gold rates in Rajasthan are influenced by international gold prices and domestic market conditions.</p>

                <h3>Common Gold Purities in Rajasthan</h3>
                <div className="seo-check-list">
                    <p style={{ marginBottom: '10px' }}><strong>22K Gold</strong><br />Used mainly for gold jewellery such as necklaces, bangles, rings, and bridal sets.</p>
                    <p><strong>24K Gold</strong><br />Used for investment purposes like gold coins and gold bars.</p>
                </div>

                <div className="seo-price-block" style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e0e0e0', margin: '20px 0' }}>
                    <h4 style={{ marginTop: 0, color: '#d4af37' }}>Indicative Gold Rate Today (Approx.)</h4>
                    <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0' }}>
                        <li style={{ marginBottom: '8px', fontSize: '1.1rem' }}>• <strong>22K Gold:</strong> ₹1,35,000 per 10 grams (Approx)</li>
                        <li style={{ marginBottom: '8px', fontSize: '1.1rem' }}>• <strong>24K Gold:</strong> ₹1,40,000 per 10 grams (Approx)</li>
                    </ul>
                    <p style={{ fontSize: '0.8rem', color: '#888', margin: 0 }}>Note: Gold prices may vary slightly between cities and jewellers due to local taxes, making charges, and market demand.</p>
                </div>

                <h2>City-Wise Gold Rate in Rajasthan</h2>
                <p>Gold prices across Rajasthan remain mostly uniform, with minor variations.</p>
                <ul className="seo-simple-list">
                    <li><Link to="/gold-rate-jodhpur" style={{ color: 'inherit', textDecoration: 'none' }}>Gold Rate in Jodhpur</Link></li>
                    <li>Gold Rate in Jaipur</li>
                    <li>Gold Rate in Udaipur</li>
                    <li>Gold Rate in Bikaner</li>
                </ul>

                <div className="seo-highlight-box" style={{ background: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
                    <p><LightbulbOutlined sx={{ color: '#2196f3', verticalAlign: 'middle', mr: 1 }} /> <strong>Local Insight:</strong><br />
                        Jodhpur is considered one of the most trusted jewellery markets in Rajasthan for verified gold jewellery, traditional craftsmanship, and fair pricing.</p>
                </div>

                <h2>Factors Affecting Gold Price in Rajasthan</h2>
                <p>Several factors influence daily gold rates in Rajasthan:</p>
                <ul className="seo-check-list">
                    <li>International gold market prices</li>
                    <li>USD to INR exchange rate</li>
                    <li>Import duties and GST</li>
                    <li>Local demand during wedding & festival seasons</li>
                    <li>Bullion supply and market sentiment</li>
                </ul>
                <p>Because of these factors, gold prices can rise or fall daily. This is why checking the latest gold rate before buying is always recommended.</p>

                <h2>Best Time to Buy Gold in Rajasthan</h2>
                <p>From years of experience serving customers across Rajasthan, these are the best times to buy gold:</p>
                <ul className="seo-check-list">
                    <li>During short-term price corrections</li>
                    <li>Before peak <Link to="/wedding-jewellery-rajasthan">wedding season</Link></li>
                    <li>On auspicious occasions like Akshaya Tritiya and Dhanteras</li>
                </ul>
                <p>Buying gold from a trusted jeweller ensures you receive correct purity, transparent pricing, and BIS hallmark certification.</p>

                <h2>Why Buy Gold from MAB Jewels?</h2>
                <p>MAB Jewels is built on the trusted foundation of <strong>Manoj Abhushan Bhandar</strong>, serving customers across Rajasthan with honesty and craftsmanship.</p>
                <ul className="seo-check-list">
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> <strong>BIS Hallmark certified gold</strong></li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> <strong>Transparent pricing</strong> aligned with live market rates</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> <strong>Trusted jewellery store</strong> in Jodhpur & Rajasthan</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> <strong>Generations of customer trust</strong></li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> <strong>Expert guidance</strong> for jewellery and gold investment</li>
                </ul>
                <p style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '15px' }}>
                    <LocationOnOutlined sx={{ color: '#d4af37' }} /> Visit our store or <Link to="/contact">contact us</Link> to get the best gold rate in Rajasthan today.
                </p>

                <h2>Frequently Asked Questions (FAQs)</h2>
                <div className="seo-faq-section">
                    <div className="seo-faq-item">
                        <h4>What is today's gold rate in Rajasthan?</h4>
                        <p>Gold rates change daily. Always check the latest updated gold price before buying jewellery or investing.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Is gold cheaper in Rajasthan?</h4>
                        <p>Gold prices are similar across India. The difference lies in jeweller transparency, purity, and making charges.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Which gold purity is best for jewellery?</h4>
                        <p>22K gold is ideal for jewellery. 24K gold is best suited for investment.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Where can I buy certified gold in Rajasthan?</h4>
                        <p>Buying from a BIS-hallmarked and trusted jeweller like MAB Jewels ensures purity and fair pricing.</p>
                    </div>
                </div>

                <div className="internal-links-section" style={{ marginTop: '40px', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
                    <h3>Explore More from MAB Jewels</h3>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <ArrowForward sx={{ color: '#d4af37', mr: 1 }} />
                            <Link to="/gold-rate-jodhpur">Check Today's Gold Rate in Jodhpur</Link>
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <ArrowForward sx={{ color: '#d4af37', mr: 1 }} />
                            <Link to="/gold-jewellery-jodhpur">View Gold Jewellery Collection</Link>
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <ArrowForward sx={{ color: '#d4af37', mr: 1 }} />
                            <Link to="/wedding-jewellery-rajasthan">Exclusive Wedding Jewellery in Rajasthan</Link>
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <ArrowForward sx={{ color: '#d4af37', mr: 1 }} />
                            <Link to="/trusted-jewellery-store-jodhpur">Trusted Jewellery Store in Jodhpur</Link>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default GoldRateRajasthanPage;
